// === SAA:CONFIG:SIDEBAR-CONTENT ===
// Typed reader for the per-brand sidebar block (config texts.sidebar) and the
// contact opening hours. Config carries STRUCTURE and i18n KEYS only, never
// literal copy: every text slot resolves a default i18n key (sidebar.advisor.*,
// sidebar.trust.*), and a brand that needs different wording sets an override
// key in its config for that one slot. All keys optional; a missing structural
// value (photo url, trust items) renders nothing.

import type { i18n as I18nInstance, TFunction } from 'i18next';
import type { SluitstraatConfig } from '@sluitstraat/core';

export interface SidebarTrustItemConfig {
  icon?: string;
  titleKey?: string;
  textKey?: string;
}

export interface SidebarTexts {
  advisorEyebrowKey?: string;
  advisorTitleKey?: string;
  advisorTextKey?: string;
  advisorPhotoUrl?: string;
  advisorPhotoAltKey?: string;
  statusTextKey?: string;
  trustEyebrowKey?: string;
  trustTitleKey?: string;
  trustItems?: SidebarTrustItemConfig[];
}

export function getSidebarTexts(config: SluitstraatConfig | null | undefined): SidebarTexts {
  return (config?.texts as { sidebar?: SidebarTexts } | undefined)?.sidebar ?? {};
}

export function getContactHours(config: SluitstraatConfig | null | undefined): string | undefined {
  return (config?.customer?.contact as { hours?: string } | undefined)?.hours;
}

/**
 * Resolve a sidebar text slot: the brand's override key wins only when it has a
 * translation in the ACTIVE language (checked per-language via getResource, so
 * i18next's fallbackLng cannot smuggle in another language's text); otherwise
 * the default key resolves through the normal translation layer. A raw key
 * string is never rendered.
 */
export function resolveSidebarText(
  t: TFunction,
  i18n: I18nInstance,
  overrideKey: string | undefined,
  defaultKey: string,
): string {
  if (overrideKey && i18n.getResource(i18n.language, 'translation', overrideKey) !== undefined) {
    return t(overrideKey);
  }
  return t(defaultKey);
}

/**
 * Same resolution for OPTIONAL slots (status line): no override key configured
 * means the slot is off and renders nothing — there is no default copy.
 */
export function resolveOptionalSidebarText(
  t: TFunction,
  i18n: I18nInstance,
  overrideKey: string | undefined,
): string | undefined {
  if (!overrideKey) return undefined;
  if (i18n.getResource(i18n.language, 'translation', overrideKey) === undefined) return undefined;
  return t(overrideKey);
}
